import React, { useEffect, useRef, useState } from "react"
import L10n from "lib/carrot-l10n"
import { FormProvider, useForm } from "react-hook-form"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect,
  DateOfBirth
} from "../../shared"
import moment, { Moment } from "moment"
import { Container, H2, H4 } from "@carrotfertility/carotene"
import { useStateMachine } from "components/context/stateMachine/StateMachineContext"
import { Steps } from "components/csd-enrollment/workflow/steps"
import useSubmitWithErrorCatch from "components/csd-enrollment/hooks/useSubmitWithErrorCatch"
import { Form } from "components/views/atoms/forms/Form"
import { useCurrentUser } from "components/context/user/UserContext"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"

interface DobSexStepProps {
  defaultSex: string | null
  defaultDateOfBirth: string | null
  viewName: typeof Steps[keyof typeof Steps]
  onUserUpdate: (dateOfBirth: string, sex: string) => any
  selectionLabels: {
    dob: string
    sex: string
  }
}

interface UseDateOfBirthInputType {
  isDateOfBirthValid: () => boolean
  validDateOfBirth: boolean
  dateOfBirth: Moment | null
  dateOfBirthRef: any
}

const l = new L10n({ locale: "en-US" }).getBoundLocalize()
const FieldNameSex = "sexValue"

function useDateOfBirthInput(defaultDateOfBirth: null | string): UseDateOfBirthInputType {
  const dateOfBirthRef = useRef(null)
  const [dateOfBirth, setDateOfBirth] = useState(null)
  const [validDateOfBirth, setValidDateOfBirth] = useState(true)

  function isDateOfBirthValid(): boolean {
    const dobInputIsValid = dateOfBirthRef.current.validate()
    setValidDateOfBirth(dobInputIsValid)
    return dobInputIsValid
  }

  useEffect(() => {
    if (defaultDateOfBirth != null) {
      setDateOfBirth(moment(defaultDateOfBirth))
    }
  }, [defaultDateOfBirth])

  return {
    isDateOfBirthValid,
    validDateOfBirth,
    dateOfBirth,
    dateOfBirthRef
  }
}

export const enum SexOptions {
  MALE = "MALE",
  FEMALE = "FEMALE",
  INTERSEX = "INTERSEX"
}

const sexIdToLabel = {
  [SexOptions.MALE]: "Male",
  [SexOptions.FEMALE]: "Female",
  [SexOptions.INTERSEX]: "Intersex"
}

export const getSexLabelFromId = (id: SexOptions): string => sexIdToLabel[id]

function DobSexStep({
  defaultSex,
  defaultDateOfBirth,
  viewName,
  selectionLabels,
  onUserUpdate
}: DobSexStepProps): JSX.Element {
  const shouldSkip = defaultSex && defaultDateOfBirth
  const { setNextStep } = useStateMachine(viewName, shouldSkip ? "" : null)

  const formMethods = useForm()
  const { isDateOfBirthValid, validDateOfBirth, dateOfBirth, dateOfBirthRef } = useDateOfBirthInput(defaultDateOfBirth)

  const options = [
    { label: getSexLabelFromId(SexOptions.MALE), id: SexOptions.MALE },
    { label: getSexLabelFromId(SexOptions.FEMALE), id: SexOptions.FEMALE },
    {
      label: getSexLabelFromId(SexOptions.INTERSEX),
      id: SexOptions.INTERSEX,
      filter: (c) => c.featureConfig.isLgtbqEnabled
    }
  ]

  const { handleSubmit, setValue } = formMethods
  useEffect(() => {
    setValue(
      FieldNameSex,
      options.find(({ id }) => id === defaultSex)
    )
  }, [])

  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    if (!isDateOfBirthValid()) return
    const sexVal = args[FieldNameSex].id
    const dobVal = dateOfBirthRef.current.getMoment().format("YYYY-MM-DD")

    // We should try to use a hidden input / more native react-hook-forms methods after we refactor the dobSelector
    await onUserUpdate(dobVal, sexVal)
    setNextStep(viewName, "")
  })

  const benefitConfiguration = useSelector(getBenefitConfiguration)
  const filteredSexOptions = options.filter((c) => !c.filter || c.filter(benefitConfiguration))

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Container padding="none" stack="huge">
          <BenefitEnrollmentFlowModalBackButton />
        </Container>
        <H2 stack="medium" variant="primary">
          {selectionLabels.sex}
        </H2>
        <H4 stack="tiny" variant="primary">{l`SEX`}</H4>
        <Container padding="none" stack="small">
          <BenefitEnrollmentModalFormSelect name={FieldNameSex} options={filteredSexOptions} />
        </Container>
        <H2 stack="large" variant="primary">
          {selectionLabels.dob}
        </H2>
        <Container padding="none" stack="giant">
          <DateOfBirth {...{ dateOfBirthRef, dateOfBirth, validDateOfBirth }} />
        </Container>
        <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
      </Form>
    </FormProvider>
  )
}

export function MemberDobSexStep(): JSX.Element {
  const { sex: defaultSex, dateOfBirth: defaultDateOfBirth } = useCurrentUser()
  const { updateUserInfo } = useCurrentUser()
  function onUserUpdate(dateOfBirth, sex): any {
    return updateUserInfo({
      sex,
      dateOfBirth
    })
  }
  return (
    <DobSexStep
      selectionLabels={{
        sex: l`Your sex, as stated on your health insurance plan`,
        dob: l`Your date of birth, as stated on your health insurance plan`
      }}
      viewName={Steps.MEMBER_DOB_SEX_CONNECT}
      {...{ defaultSex, defaultDateOfBirth, onUserUpdate }}
    />
  )
}

export function PartnerDobSexStep(): JSX.Element {
  const { partnerSex: defaultSex, partnerDateOfBirth: defaultDateOfBirth } = useCurrentUser()
  const { updateUserInfo } = useCurrentUser()

  function onUserUpdate(dateOfBirth, sex): any {
    return updateUserInfo({
      partnerSex: sex,
      partnerDateOfBirth: dateOfBirth
    })
  }
  return (
    <DobSexStep
      selectionLabels={{
        sex: l`Your partner’s sex, as stated on their health insurance plan`,
        dob: l`Your partner’s date of birth, as stated on their health insurance plan`
      }}
      viewName={Steps.PARTNER_DOB_SEX_CONNECT}
      {...{ defaultSex, defaultDateOfBirth, onUserUpdate }}
    />
  )
}
